import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const emptyForm = {
  title: "",
  slug: "",
  body: "",
  meta_title: "",
  meta_description: "",
  is_published: true,
};

function PagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axiosInstance.get("/api/pages");
      setPages(res.data);
    } catch (err) {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setImageFile(null);
    setExistingImage(null);
    setShowModal(true);
  };

  const openEditModal = (page) => {
    setEditingId(page.id);
    setForm({
      title: page.title,
      slug: page.slug,
      body: page.body || "",
      meta_title: page.meta_title || "",
      meta_description: page.meta_description || "",
      is_published: page.is_published,
    });
    setImageFile(null);
    setExistingImage(page.featured_image_path || null);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const formData = new FormData();
    formData.append("title", form.title);
    if (form.slug) formData.append("slug", form.slug);
    formData.append("body", form.body);
    formData.append("meta_title", form.meta_title);
    formData.append("meta_description", form.meta_description);
    formData.append("is_published", form.is_published);
    if (imageFile) formData.append("featured_image", imageFile);

    try {
      if (editingId) {
        await axiosInstance.put(`/api/pages/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Page updated");
      } else {
        await axiosInstance.post("/api/pages", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Page created");
      }
      setShowModal(false);
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save page");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete page "${title}"?`)) return;
    try {
      await axiosInstance.delete(`/api/pages/${id}`);
      toast.success("Page deleted");
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Custom Pages</h4>
        <button className="btn btn-primary" onClick={openAddModal}>
          <i className="bi bi-plus-lg me-1"></i> New Page
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "60px" }}></th>
                <th>Title</th>
                <th className="d-none d-md-table-cell">Slug (URL)</th>
                <th style={{ width: "100px" }}>Status</th>
                <th style={{ width: "100px" }} className="text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pages.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    No custom pages yet.
                  </td>
                </tr>
              ) : (
                pages.map((page) => (
                  <tr key={page.id}>
                    <td>
                      {page.featured_image_path ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${page.featured_image_path}`}
                          alt={page.title}
                          className="rounded"
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="bg-light rounded d-flex align-items-center justify-content-center"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <i className="bi bi-file-earmark-text text-muted"></i>
                        </div>
                      )}
                    </td>
                    <td className="fw-medium">{page.title}</td>
                    <td className="d-none d-md-table-cell text-muted small">
                      /{page.slug}
                    </td>
                    <td>
                      <span
                        className={`badge ${page.is_published ? "bg-success" : "bg-secondary"}`}
                      >
                        {page.is_published ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => openEditModal(page)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(page.id, page.title)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingId ? "Edit Page" : "New Page"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        URL Slug{" "}
                        <small className="text-muted">
                          (leave blank to auto-generate from title)
                        </small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">/pages/</span>
                        <input
                          type="text"
                          className="form-control"
                          name="slug"
                          placeholder={editingId ? form.slug : "auto-generated"}
                          value={form.slug}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Featured Image</label>
                      {existingImage && !imageFile && (
                        <div className="mb-2">
                          <img
                            src={`${process.env.REACT_APP_API_URL}/${existingImage}`}
                            alt="Current"
                            className="rounded"
                            style={{
                              width: "80px",
                              height: "80px",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={(e) => setImageFile(e.target.files[0])}
                      />
                      {editingId && (
                        <small className="text-muted">
                          Leave empty to keep the current image
                        </small>
                      )}
                      <small className="d-block text-muted mt-1">
                        Optional — e.g. a chairman's photo, a banner, or any
                        relevant image
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Content</label>
                      <textarea
                        className="form-control"
                        name="body"
                        rows="6"
                        value={form.body}
                        onChange={handleChange}
                      ></textarea>
                    </div>

                    <div className="row">
                      <div className="col-6 mb-3">
                        <label className="form-label">Meta Title (SEO)</label>
                        <input
                          type="text"
                          className="form-control"
                          name="meta_title"
                          value={form.meta_title}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <label className="form-label">
                          Meta Description (SEO)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="meta_description"
                          value={form.meta_description}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="is_published"
                        name="is_published"
                        checked={form.is_published}
                        onChange={handleChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="is_published"
                      >
                        Published (visible on the public site)
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : editingId ? "Update" : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
}

export default PagesPage;
